import { useState, useEffect, useRef } from 'react';
import { mergeParsedSchedule, validateScheduleForm, confirmScheduleConflict } from './scheduleForm';
import { API_BASE_URL, getDispatcherToken } from './DispatcherApp';
import InfantSeatSelector from '../../components/InfantSeatSelector';

export default function AddScheduleModal({ driver, drivers, dispatcher, onClose, onSuccess }) {
  const [mode, setMode] = useState('manual');
  const [selectedDriverId, setSelectedDriverId] = useState(driver?.id || '');
  const [rawText, setRawText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [form, setForm] = useState({
    date: '',
    time: '',
    pickup_location: '',
    dropoff_location: '',
    price_amount: '',
    price_currency: 'JPY',
    notes: '',
    service_type: 'transfer',
    customer_name: '',
    customer_company: '',
    customer_contact: '',
    customer_code: '',
    flight_number: '',
    passenger_count: '',
    luggage_count: '',
    infant_seat_count: 0,
    vehicle_type: '',
    internal_notes: '',
    driver_collection_amount: '',
    driver_collection_currency: 'JPY',
  });
  const [conflicts, setConflicts] = useState([]);
  const [checkingConflict, setCheckingConflict] = useState(false);
  const [conflictConfirmed, setConflictConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const conflictGeneration = useRef(0);
  const conflictKey = `${selectedDriverId}|${form.date}|${form.time}`;
  const [checkedKey, setCheckedKey] = useState('');
  useEffect(() => {
    const counter = conflictGeneration;
    const generation = ++conflictGeneration.current;
    setConflicts([]);
    setConflictConfirmed(false);
    setCheckedKey('');
    if (!selectedDriverId || !form.date || !form.time) { setCheckingConflict(false); return; }
    const controller = new AbortController();
    setCheckingConflict(true);
    fetch(`${API_BASE_URL}/dispatcher/extra-schedule/check-conflict`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getDispatcherToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ driver_id: selectedDriverId, date: form.date, time: form.time }),
      signal: controller.signal,
    }).then(async response => {
      if (!response.ok) throw new Error('无法检查排班冲突，请稍后重试');
      const data = await response.json();
      if (generation === conflictGeneration.current) {
        setConflicts(data.conflicts || []); setCheckedKey(conflictKey);
      }
    }).catch(err => {
      if (err.name !== 'AbortError' && generation === conflictGeneration.current) setError(err.message);
    }).finally(() => {
      if (generation === conflictGeneration.current) setCheckingConflict(false);
    });
    return () => { controller.abort(); counter.current++; };
  }, [selectedDriverId, form.date, form.time, conflictKey]);

  const handleAIParse = async () => {
    if (!rawText.trim()) {
      setError('请输入预约信息');
      return;
    }

    setParsing(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/dispatcher/extra-schedule/parse`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getDispatcherToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ raw_text: rawText })
      });

      const data = await response.json();
      if (data.success && data.parsed) {
        const parsed = data.parsed;
        setForm(current => mergeParsedSchedule(current, parsed));
        
        if (data.missing_fields?.length > 0) {
          const fieldNames = {
            date: '日期',
            time: '时间',
            pickup_location: '出发地',
            dropoff_location: '目的地',
            price_amount: '金额'
          };
          const missing = data.missing_fields.map(f => fieldNames[f] || f).join('、');
          setError(`AI未能识别: ${missing}，请手动补充`);
        }
      } else {
        setError(data.error || 'AI识别失败，请手动输入');
      }
    } catch (err) {
      setError('AI识别失败，请手动输入');
    } finally {
      setParsing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!selectedDriverId) {
      setError('请选择司机');
      return;
    }
    if (!form.date || !form.time || !form.pickup_location || !form.dropoff_location) {
      setError('日期、时间、出发地、目的地为必填项');
      return;
    }
    const validationError = validateScheduleForm(form);
    if (validationError) { setError(validationError); return; }
    const priceAmount = Number(form.price_amount);
    if (form.price_amount === '' || Number.isNaN(priceAmount) || priceAmount < 0) {
      setError('请输入有效的金额');
      return;
    }

    if (conflicts.length > 0 && !conflictConfirmed) {
      setError('发现时间冲突，请确认后再提交');
      return;
    }

    setSubmitting(true);
    try {
      if (!(await confirmScheduleConflict(API_BASE_URL, getDispatcherToken(), {
        driver_id: selectedDriverId, date: form.date, time: form.time,
      }))) return;
      const response = await fetch(`${API_BASE_URL}/dispatcher/extra-schedule`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getDispatcherToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          driver_id: selectedDriverId,
          raw_text: rawText || null,
          date: form.date,
          time: form.time,
          pickup_location: form.pickup_location,
          dropoff_location: form.dropoff_location,
          price_amount: priceAmount,
          price_currency: form.price_currency,
          service_type: form.service_type || 'transfer',
          vehicle_type: form.vehicle_type || null,
          notes: form.notes || null,
          internal_notes: form.internal_notes || null,
          customer_name: form.customer_name || null,
          customer_company: form.customer_company || null,
          customer_contact: form.customer_contact || null,
          customer_code: form.customer_code || null,
          flight_number: form.flight_number || null,
          passenger_count: form.passenger_count === '' ? null : Number(form.passenger_count),
          luggage_count: form.luggage_count === '' ? null : Number(form.luggage_count),
          infant_seat_count: Number(form.infant_seat_count || 0),
          driver_collection_amount: form.driver_collection_amount === '' ? null : Number(form.driver_collection_amount),
          driver_collection_currency: form.driver_collection_currency || null,
          conflict_confirmed: true
        })
      });

      if (response.ok) {
        onSuccess();
      } else {
        const data = await response.json();
        setError(data.detail || '创建失败');
      }
    } catch (err) {
      setError('创建失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="dispatcher-form-modal bg-card rounded-xl shadow-lg w-full max-w-xl max-h-[90vh] flex flex-col border border-border-subtle">
        {/* Header */}
        <div className="p-4 border-b border-border-subtle flex justify-between items-center bg-surface rounded-t-lg">
          <div>
            <h2 className="text-base font-semibold text-foreground">添加调度排班</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {driver ? `为 ${driver.name} 添加排班` : '选择司机并添加排班'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded hover:bg-muted transition"
          >
            <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Mode Toggle */}
        <div className="p-3 border-b border-border-subtle bg-card">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMode('manual')}
              className={`px-3 py-1.5 rounded text-xs font-medium transition ${
                mode === 'manual' 
                  ? 'premium-button bg-primary text-primary-foreground'
                  : 'bg-surface text-muted-foreground hover:bg-muted'
                }`}
              >
              手动输入
            </button>
            <button
              type="button"
              onClick={() => setMode('ai')}
              className={`px-3 py-1.5 rounded text-xs font-medium transition ${
                mode === 'ai' 
                  ? 'premium-button bg-primary text-primary-foreground'
                  : 'bg-surface text-muted-foreground hover:bg-muted'
                }`}
              >
              AI识别
            </button>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
          {error && (
            <div className="premium-chip-destructive px-3 py-2 rounded text-xs">
              {error}
            </div>
          )}

          {/* AI Mode */}
          {mode === 'ai' && (
            <div className="space-y-2">
              <label className="block text-xs font-medium text-foreground">
                粘贴预约信息（支持中文/日文/英文）
              </label>
              <textarea
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                placeholder="例如：3月25日早上9点，成田机场T1接人，送到六本木希尔顿，35000日元"
                className="w-full px-3 py-2 border border-input rounded-xl focus:ring-1 focus:ring-ring h-24 resize-none"
                data-testid="ai-raw-text"
              />
              <button
                type="button"
                onClick={handleAIParse}
                disabled={parsing}
                className="px-3 py-1.5 premium-button bg-primary text-primary-foreground rounded text-xs hover:bg-primary/90 transition disabled:opacity-50 flex items-center gap-1.5"
              >
                {parsing ? '识别中...' : 'AI识别'}
              </button>
            </div>
          )}

          {/* Driver Selection */}
          {!driver && (
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">司机 *</label>
              <select
                value={selectedDriverId}
                onChange={(e) => setSelectedDriverId(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-xl focus:ring-1 focus:ring-ring"
                required
                data-testid="driver-select"
              >
                <option value="">请选择司机</option>
                {drivers.map(d => (
                  <option key={d.id} value={d.id}>{d.name} ({d.phone})</option>
                ))}
              </select>
            </div>
          )}

          {/* Form Fields */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">日期 *</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="w-full px-3 py-2 border border-input rounded-xl focus:ring-1 focus:ring-ring"
                required
                data-testid="date-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">时间 *</label>
              <input
                type="time"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                className="w-full px-3 py-2 border border-input rounded-xl focus:ring-1 focus:ring-ring"
                required
                data-testid="time-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground mb-1">出发地 *</label>
            <input
              type="text"
              value={form.pickup_location}
              onChange={(e) => setForm({ ...form, pickup_location: e.target.value })}
              placeholder="例如：成田空港T1"
              className="w-full px-3 py-2 border border-input rounded-xl focus:ring-1 focus:ring-ring"
              required
              data-testid="pickup-input"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground mb-1">目的地 *</label>
            <input
              type="text"
              value={form.dropoff_location}
              onChange={(e) => setForm({ ...form, dropoff_location: e.target.value })}
              placeholder="例如：六本木ヒルズ"
              className="w-full px-3 py-2 border border-input rounded-xl focus:ring-1 focus:ring-ring"
              required
              data-testid="dropoff-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">金额 *</label>
              <input
                type="number"
                step={form.price_currency === 'CNY' ? '0.01' : '1'}
                inputMode="decimal"
                value={form.price_amount}
                onChange={(e) => setForm({ ...form, price_amount: e.target.value })}
                placeholder="例如：35000"
                className="w-full px-3 py-2 border border-input rounded-xl focus:ring-1 focus:ring-ring"
                required
                min="0"
                data-testid="price-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">币种 *</label>
              <select
                value={form.price_currency}
                onChange={(e) => setForm({ ...form, price_currency: e.target.value })}
                className="w-full px-3 py-2 border border-input rounded-xl focus:ring-1 focus:ring-ring"
                data-testid="currency-select"
              >
                <option value="JPY">JPY (日元)</option>
                <option value="CNY">CNY (人民币)</option>
              </select>
            </div>
          </div>

          {/* Order Type */}
          <div>
            <label className="block text-xs font-medium text-foreground mb-1">订单类型</label>
            <select
              value={form.service_type}
              onChange={(e) => setForm({ ...form, service_type: e.target.value })}
              className="w-full px-3 py-2 border border-input rounded-xl focus:ring-1 focus:ring-ring"
            >
              <option value="transfer">接送</option>
              <option value="charter">包车</option>
              <option value="other">其他</option>
            </select>
          </div>

          {/* Customer Info */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">客人姓名</label>
              <input
                type="text"
                value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                placeholder="客人姓名"
                className="w-full px-3 py-2 border border-input rounded-xl focus:ring-1 focus:ring-ring"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">公司名</label>
              <input
                type="text"
                value={form.customer_company}
                onChange={(e) => setForm({ ...form, customer_company: e.target.value })}
                placeholder="公司名"
                className="w-full px-3 py-2 border border-input rounded-xl focus:ring-1 focus:ring-ring"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">联系方式</label>
              <input
                type="text"
                value={form.customer_contact}
                onChange={(e) => setForm({ ...form, customer_contact: e.target.value })}
                placeholder="电话/邮箱"
                className="w-full px-3 py-2 border border-input rounded-xl focus:ring-1 focus:ring-ring"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">客户代码</label>
              <input
                type="text"
                value={form.customer_code}
                onChange={(e) => setForm({ ...form, customer_code: e.target.value })}
                placeholder="YM-67"
                className="w-full px-3 py-2 border border-input rounded-xl focus:ring-1 focus:ring-ring"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">航班号</label>
              <input
                type="text"
                value={form.flight_number}
                onChange={(e) => setForm({ ...form, flight_number: e.target.value })}
                placeholder="NH879"
                className="w-full px-3 py-2 border border-input rounded-xl focus:ring-1 focus:ring-ring"
              />
            </div>
          </div>

          <InfantSeatSelector value={form.infant_seat_count} onChange={infant_seat_count => setForm({ ...form, infant_seat_count })} />
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">乘客人数</label>
              <input
                type="number"
                value={form.passenger_count}
                onChange={(e) => setForm({ ...form, passenger_count: e.target.value })}
                placeholder="人数"
                className="w-full px-3 py-2 border border-input rounded-xl focus:ring-1 focus:ring-ring"
                min="0"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">行李数量</label>
              <input
                type="number"
                value={form.luggage_count}
                onChange={(e) => setForm({ ...form, luggage_count: e.target.value })}
                placeholder="件数"
                className="w-full px-3 py-2 border border-input rounded-xl focus:ring-1 focus:ring-ring"
                min="0"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">车辆类型</label>
              <input
                type="text"
                value={form.vehicle_type}
                onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })}
                placeholder="alphard"
                className="w-full px-3 py-2 border border-input rounded-xl focus:ring-1 focus:ring-ring"
              />
            </div>
          </div>

          {/* Driver collection */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">司机代收金额</label>
              <input
                type="number"
                step={form.driver_collection_currency === 'CNY' ? '0.01' : '1'}
                min="0"
                value={form.driver_collection_amount}
                onChange={(e) => setForm({ ...form, driver_collection_amount: e.target.value })}
                placeholder="0"
                className="w-full px-3 py-2 border border-input rounded-xl focus:ring-1 focus:ring-ring"
                min="0"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">代收币种</label>
              <select
                value={form.driver_collection_currency}
                onChange={(e) => setForm({ ...form, driver_collection_currency: e.target.value })}
                className="w-full px-3 py-2 border border-input rounded-xl focus:ring-1 focus:ring-ring"
              >
                <option value="JPY">JPY (日元)</option>
                <option value="CNY">CNY (人民币)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground mb-1">备注</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="可选备注信息"
              className="w-full px-3 py-2 border border-input rounded-xl focus:ring-1 focus:ring-ring h-16 resize-none"
              data-testid="notes-input"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground mb-1">内部备注（仅管理/调度可见）</label>
            <textarea
              value={form.internal_notes}
              onChange={(e) => setForm({ ...form, internal_notes: e.target.value })}
              placeholder="客人特殊要求、调度备注、司机注意事项..."
              className="w-full px-3 py-2 border border-warning/40 bg-warning/10 text-warning-foreground rounded-xl focus:ring-1 focus:ring-warning h-16 resize-none"
            />
          </div>

          {/* Conflict Warning */}
          {conflicts.length > 0 && (
            <div className="border border-warning/40 bg-warning/10 rounded-xl p-3">
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 text-warning-foreground flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div className="flex-1">
                  <h4 className="text-warning-foreground font-medium text-xs">发现时间冲突</h4>
                  <p className="text-warning text-xs mt-0.5">该司机在此时间段已有以下排班:</p>
                  <ul className="mt-1.5 space-y-0.5">
                    {conflicts.map((c, i) => (
                      <li key={i} className="text-xs text-warning">
                        • {c.source_label}: {c.time} {c.pickup_location} → {c.dropoff_location}
                      </li>
                    ))}
                  </ul>
                  <label className="flex items-center gap-1.5 mt-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={conflictConfirmed}
                      onChange={(e) => setConflictConfirmed(e.target.checked)}
                      className="w-3.5 h-3.5 text-warning rounded"
                    />
                    <span className="text-xs text-warning-foreground">我已确认，仍要添加此排班</span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="p-3 border-t border-border-subtle bg-surface flex justify-end gap-2 rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 premium-button bg-surface text-foreground rounded text-xs hover:bg-muted transition"
          >
            取消
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={submitting || parsing || checkingConflict || (selectedDriverId && form.date && form.time && checkedKey !== conflictKey) || (conflicts.length > 0 && !conflictConfirmed)}
            className="px-4 py-1.5 premium-button bg-primary text-primary-foreground rounded text-xs hover:bg-primary/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="submit-schedule-btn"
          >
            {submitting ? '提交中...' : '确认添加'}
          </button>
        </div>
      </div>
    </div>
  );
}
